import { Mark, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

export interface CommentMarkOptions {
  HTMLAttributes: Record<string, any>;
  onCommentClick?: (commentId: string) => void;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    commentMark: {
      setCommentMark: (attributes: { commentId: string }) => ReturnType;
      unsetCommentMark: (commentId?: string) => ReturnType;
    };
  }
}

export const CommentMark = Mark.create<CommentMarkOptions>({
  name: "commentMark",

  addOptions() {
    return {
      HTMLAttributes: {},
      onCommentClick: undefined,
    };
  },

  // Comments don't round-trip through markdown; they're re-applied on load.
  // Allow overlapping marks (multiple comments on same text).
  excludes: "",
  inclusive: false,
  spanning: true,

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-comment-id"),
        renderHTML: (attrs) =>
          attrs.commentId ? { "data-comment-id": attrs.commentId } : {},
      },
      resolved: {
        default: false,
        parseHTML: (el) => el.getAttribute("data-resolved") === "true",
        renderHTML: (attrs) =>
          attrs.resolved ? { "data-resolved": "true" } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-comment-id]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const resolved = HTMLAttributes["data-resolved"] === "true";
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: resolved ? "comment-mark comment-mark-resolved" : "comment-mark",
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setCommentMark:
        (attributes) =>
        ({ commands }) =>
          commands.setMark(this.name, attributes),
      unsetCommentMark:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },

  addProseMirrorPlugins() {
    const onCommentClick = this.options.onCommentClick;
    const markType = this.type;
    return [
      new Plugin({
        key: new PluginKey("commentMarkClick"),
        props: {
          handleClick(view, pos) {
            if (!onCommentClick) return false;
            const resolved = view.state.doc.resolve(pos);
            const marks = resolved.marks();
            for (const mark of marks) {
              if (mark.type === markType && mark.attrs.commentId) {
                onCommentClick(mark.attrs.commentId);
                return true;
              }
            }
            return false;
          },
        },
      }),
    ];
  },
});
